import React from 'react';

// services
import {CreditBookApi} from '../../shared/services/ApiService';

// helpers
import {Trans, useTranslation} from 'react-i18next';
import {useQuery} from '@tanstack/react-query';

// components
import {CardError, PageTemplate} from '../../shared/components';
import {Information, Address, CardProfileSkeleton} from './components';

// styles
import {LinkPrimary, TextDefault} from '../../shared/styles';
import {
  ProfileDetails,
  ProfileWrapper,
  LinkContactWrapper,
} from './ProfilePage-styles';

// types
import {
  CreditBookApiError,
  UserInformation,
} from '../../shared/services/ApiService/types';

/**
 * Get user information
 */
const getUserInformation = async (): Promise<UserInformation['data']> => {
  const {data} = await CreditBookApi.user.getUserInformation();
  return data;
};

const ProfilePage = () => {
  const {t} = useTranslation();
  const {
    data: userInformation,
    error,
    isLoading,
    isRefetching,
  } = useQuery<UserInformation['data'], CreditBookApiError>({
    queryKey: ['userInformation'],
    queryFn: getUserInformation,
  });

  const renderProfileDetails = () => {
    if (isLoading || isRefetching) {
      return (
        <ProfileDetails>
          <CardProfileSkeleton numberOfLines={4} />
          <CardProfileSkeleton numberOfLines={6} />
        </ProfileDetails>
      );
    } else {
      return (
        <ProfileDetails>
          <Information
            basicInformation={userInformation?.basicInformation || {}}
          />
          <Address address={userInformation?.address || {}} />
        </ProfileDetails>
      );
    }
  };

  return (
    <PageTemplate title={t('profile')}>
      {error ? (
        <CardError message={error?.response?.data?.errorMessage ?? undefined} />
      ) : (
        <ProfileWrapper>
          {renderProfileDetails()}
          <LinkContactWrapper>
            <TextDefault>
              <Trans
                i18nKey="profilePage_ifError"
                t={t}
                components={{
                  contact: (
                    <LinkPrimary href="/contact">
                      {t('profilePage_contactUs')}
                    </LinkPrimary>
                  ),
                }}
              />
            </TextDefault>
          </LinkContactWrapper>
        </ProfileWrapper>
      )}
    </PageTemplate>
  );
};

export default ProfilePage;
